import { useCallback, useEffect, useRef, useState } from 'react';

export function useMicStream() {
  const [permission, setPermission] = useState<
    'pending' | 'prompting' | 'granted' | 'denied'
  >('pending');
  const [level, setLevel] = useState(0);
  const [isSpeaking, setIsSpeaking] = useState(false);

  const streamRef = useRef<MediaStream | null>(null);
  const ctxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const rafRef = useRef<number | null>(null);
  const silenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isSpeakingRef = useRef(false);
  const activeRef = useRef(false);

  const stopMonitoring = useCallback(() => {
    activeRef.current = false;
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (silenceTimerRef.current !== null) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    setLevel(0);
    setIsSpeaking(false);
    isSpeakingRef.current = false;
  }, []);

  const releaseStream = useCallback(() => {
    stopMonitoring();
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (ctxRef.current) {
      void ctxRef.current.close();
      ctxRef.current = null;
    }
    analyserRef.current = null;
  }, [stopMonitoring]);

  const startMonitoring = useCallback(() => {
    const analyser = analyserRef.current;
    if (!analyser || activeRef.current) return;

    activeRef.current = true;
    const dataArray = new Uint8Array(analyser.fftSize);

    const tick = () => {
      if (!activeRef.current) return;

      analyser.getByteTimeDomainData(dataArray);
      let peak = 0;
      for (let i = 0; i < dataArray.length; i++) {
        const amplitude = Math.abs((dataArray[i] ?? 128) - 128);
        if (amplitude > peak) peak = amplitude;
      }
      const normalized = peak / 128;
      setLevel(normalized);

      const isSoundDetected = normalized > 0.02;

      if (isSoundDetected) {
        if (silenceTimerRef.current !== null) {
          clearTimeout(silenceTimerRef.current);
          silenceTimerRef.current = null;
        }
        if (!isSpeakingRef.current) {
          isSpeakingRef.current = true;
          setIsSpeaking(true);
        }
      } else if (isSpeakingRef.current && silenceTimerRef.current === null) {
        silenceTimerRef.current = setTimeout(() => {
          isSpeakingRef.current = false;
          setIsSpeaking(false);
          silenceTimerRef.current = null;
        }, 500);
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
  }, []);

  const requestPermission = useCallback(async () => {
    if (permission === 'granted') return true;
    setPermission('prompting');

    try {
      // NOT web-only, unlike the two sibling suppressions in `@heidi/capture` and
      // `@heidi/integrations`: the onboarding mic test has no `isTauri()` branch
      // anywhere, so on desktop this reads a WebView permission that is not the
      // capture authority — audio there is captured natively, and a WebView2
      // per-origin `denied` can refuse a recording the OS actually allows. The
      // warning is therefore accurate, and it is INHERITED rather than
      // introduced — this line reported it at
      // `src/features/onboarding-v2/…/hooks/useMicStream.ts`, and the rule is
      // scoped `src/**` in both `.eslintrc.js` and `.oxlintrc.json`, so the move
      // into a package silenced it with nothing going red. The directive is what
      // keeps it written down until that scope reaches `packages/**`. The fix is
      // the flow's, not this line's: `MicPermissionsStep.tsx` still warns for the
      // same reason and owns the same single permission decision.
      // eslint-disable-next-line @heidi/rules/no-unguarded-browser-media -- desktop-reachable and unguarded, inherited from the app path this moved from; the onboarding mic step owes the isTauri branch as a whole
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const ctx = new AudioContext();
      ctxRef.current = ctx;

      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 2048;
      analyser.smoothingTimeConstant = 0.3;
      source.connect(analyser);
      analyserRef.current = analyser;

      setPermission('granted');
      return true;
    } catch {
      setPermission('denied');
      return false;
    }
  }, [permission]);

  useEffect(() => {
    return () => releaseStream();
  }, [releaseStream]);

  return {
    permission,
    level,
    isSpeaking,
    requestPermission,
    startMonitoring,
    stopMonitoring,
  };
}
